import type { Room, RoomOptions } from '@infiltre/shared';
import type { Api } from '@/shared/lib/api';

// REST wrappers around the rooms module. `Api` is injected because platform
// context owns the base URL — no module-scoped singleton.

const formatErr = (err: { value: unknown }): string => {
  const v = err.value;
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && 'error' in v && typeof v.error === 'string') return v.error;
  return JSON.stringify(v);
};

export interface RoomEnvelope {
  room: Room;
  code: string;
  roomId: string;
}

export const createRoomRest = async (
  api: Api,
  input: { name: string; options?: Partial<RoomOptions> },
): Promise<RoomEnvelope> => {
  const res = await api.rooms.post({
    name: input.name,
    options: input.options,
  });
  if (res.error) throw new Error(formatErr(res.error));
  return res.data as RoomEnvelope;
};

export const joinRoomRest = async (
  api: Api,
  input: { code: string; name: string },
): Promise<RoomEnvelope> => {
  const res = await api.rooms({ code: input.code }).join.post({ name: input.name });
  if (res.error) throw new Error(formatErr(res.error));
  return res.data as RoomEnvelope;
};

/** Channel-bound idempotent create-or-join used by the Discord Activity client. */
export const externalJoinRoomRest = async (
  api: Api,
  input: {
    externalKey: string;
    name: string;
    options?: Partial<RoomOptions>;
  },
): Promise<RoomEnvelope> => {
  const res = await api.rooms['external-join'].post({
    name: input.name,
    externalKey: input.externalKey,
    options: input.options,
  });
  if (res.error) throw new Error(formatErr(res.error));
  return res.data as RoomEnvelope;
};
